import { useTheme } from "../hooks/use-theme";
import React, { useMemo } from "react";
import { WorkingTimeDurationBreakdownItemType } from "../__generated__/graphql";
import Table from "../ui_atoms/Table";
import VStack from "../ui_atoms/VStack";
import { useI18n } from "../hooks/i18n-hooks";
import { minutesAsString } from "../utils";
import Callout from "../ui_atoms/Callout";
import { View } from "react-native";
import { BodyLargeText } from "./FormattedText";

type TimeSheetReviewProps = {
    hoursBreakdown?: {
        hourRate: number;
        type: WorkingTimeDurationBreakdownItemType;
        totalDurationMinutes: number;
    }[];
    missionMinutesPerWeek?: number;
    isSubmitted?: boolean;
};

const WORKING_TYPES = [
    WorkingTimeDurationBreakdownItemType.Day,
    WorkingTimeDurationBreakdownItemType.Night,
    WorkingTimeDurationBreakdownItemType.Sunday,
    WorkingTimeDurationBreakdownItemType.Overtime,
    WorkingTimeDurationBreakdownItemType.ExtraWork,
    WorkingTimeDurationBreakdownItemType.NightExtraWork,
    WorkingTimeDurationBreakdownItemType.WorkPublicHoliday,
];
const TimeSheetReview: React.FC<TimeSheetReviewProps> = ({
    hoursBreakdown,
    missionMinutesPerWeek,
    isSubmitted = true,
}) => {
    const { gridSize, colors } = useTheme();
    const i18n = useI18n();
    const [absenceBreakdown, rateBreakdown, totalWorking, totalOvertime, totalAbsence] =
        useMemo(() => {
            const absences: Array<{
                type: WorkingTimeDurationBreakdownItemType;
                totalDurationMinutes: number;
            }> = [];
            const _rates: Record<number, number> = {
                "100": 0,
                "125": 0,
                "150": 0,
            };
            let totalWorking = 0;
            let totalAbsence = 0;
            let overtime = 0;
            for (const item of hoursBreakdown || []) {
                if (WORKING_TYPES.includes(item.type)) {
                    _rates[item.hourRate] =
                        (_rates[item.hourRate] || 0) + item.totalDurationMinutes;
                }
                if (!WORKING_TYPES.includes(item.type)) {
                    absences.push(item);
                    totalAbsence += item.totalDurationMinutes;
                } else {
                    totalWorking += item.totalDurationMinutes;
                }
            }
            const rates = Object.entries(_rates)
                .map(([k, v]) => ({ hourRate: Number(k), totalDurationMinutes: v }))
                .sort((a, b) => {
                    return a.hourRate - b.hourRate;
                });
            if (missionMinutesPerWeek && missionMinutesPerWeek > 0) {
                overtime = totalWorking + totalAbsence - missionMinutesPerWeek;
            }
            return [absences, rates, totalWorking, overtime, totalAbsence];
        }, [hoursBreakdown, missionMinutesPerWeek]);

    const totalWorkingTimeDisplay = useMemo(() => {
        return (
            <View>
                <BodyLargeText style={{ textAlign: "right" }}>
                    {isSubmitted ? minutesAsString(totalWorking, { separator: "h" }) : "--"}
                    {missionMinutesPerWeek && missionMinutesPerWeek > 0 ? (
                        <BodyLargeText color={colors.onSurfaceDisabled}>
                            {" "}
                            / {minutesAsString(missionMinutesPerWeek, { separator: "h" })}
                        </BodyLargeText>
                    ) : null}
                </BodyLargeText>
            </View>
        );
    }, [colors.onSurfaceDisabled, missionMinutesPerWeek, isSubmitted, totalWorking]);

    return (
        <VStack>
            <Table
                data={[
                    ...rateBreakdown.map((item) => ({
                        cells: [
                            {
                                value:
                                    item.hourRate > 0
                                        ? i18n.t("time_sheet.percent_hours", {
                                              percent: item.hourRate,
                                          })
                                        : i18n.t("time_sheet.non_payed_hours"),
                            },
                            {
                                bold: true,
                                value: isSubmitted
                                    ? minutesAsString(item.totalDurationMinutes, {
                                          separator: "h",
                                      })
                                    : "--",
                            },
                        ],
                    })),
                    ...absenceBreakdown.map((item) => ({
                        type: "body",
                        cells: [
                            {
                                value: i18n.t(`workingTime.type.${item.type}`),
                                textColor: colors.onSurfaceDisabled,
                            },
                            {
                                bold: true,
                                textColor: colors.onSurfaceDisabled,
                                value: isSubmitted
                                    ? minutesAsString(item.totalDurationMinutes, {
                                          separator: "h",
                                      })
                                    : "--",
                            },
                        ],
                    })),
                    {
                        hide: !isSubmitted || totalOvertime === 0,
                        cells: [
                            {
                                value: (
                                    <Callout
                                        style={{
                                            marginBottom: gridSize,
                                            marginTop: gridSize / 2,
                                        }}
                                        mode="warning"
                                        markdown={i18n.t(
                                            totalOvertime > 0
                                                ? "time_sheet.overtime_info"
                                                : "time_sheet.undertime_info",
                                            {
                                                overtime: minutesAsString(totalOvertime, {
                                                    separator: "h",
                                                }),
                                            },
                                        )}
                                    />
                                ),
                                colspan: 2,
                            },
                        ],
                    },
                    {
                        type: "footer",
                        cells: [
                            {
                                value: <View />,
                            },
                            {
                                value: <View />,
                            },
                        ],
                    },
                    {
                        type: "footer",
                        cells: [
                            {
                                bold: true,
                                highlight: false,
                                value: i18n.t("time_sheet.total_working"),
                            },
                            {
                                bold: true,
                                highlight: false,
                                value: totalWorkingTimeDisplay,
                            },
                        ],
                    },
                    {
                        type: "footer",
                        cells: [
                            {
                                bold: true,
                                highlight: false,
                                value: i18n.t("time_sheet.total_absence"),
                                textColor: colors.onSurfaceDisabled,
                            },
                            {
                                bold: true,
                                highlight: false,
                                value: isSubmitted
                                    ? minutesAsString(totalAbsence, { separator: "h" })
                                    : "--",
                                textColor: colors.onSurfaceDisabled,
                            },
                        ],
                    },
                ]}
            />
        </VStack>
    );
};

export default TimeSheetReview;
